import React, { useEffect, useState } from 'react';
import useRequest from 'hooks/useRequest';
import { useAuth } from 'context/AuthContext';
import { useNavigation } from '../../../context/NavigationContext';
import MapInitializer from '../../Map/components/MapInitializer';
import {
  BringzzPageContainer,
  BringzzButton,
  BringzzIcon,
  BringzzInput,
  BringzzText,
  BringzzCountryDropdown,
  BringzzModal,
  BringzzBottomDrawer,
  BringzzRadio,
  BringzzRadioList
} from '@bringzz/components';
import { useTranslation } from 'react-i18next';

const COUNTRIES = [
  { code: 'AT', name: 'Austria' },
  { code: 'DE', name: 'Germany' },
  { code: 'CH', name: 'Switzerland' }
];

const EditAddressView = () => {
  const { backPress, navigate } = useNavigation();
  const { sendRequest } = useRequest();
  const { user, updateUserAddresses } = useAuth();
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [formValues, setFormValues] = useState({
    streetName: '',
    houseNumber: '',
    entrance: '',
    floor: '',
    door: '',
    postalCode: '',
    cityName: '',
    note: '',
  });
  const [country, setCountry] = useState({
    name: 'Austria',
    code: 'AT',
  });
  const [selectedLabel, setSelectedLabel] = useState(null);
  const [integrationId, setIntegrationId] = useState(null);
  const [countryDrawerOpen, setCountryDrawerOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(user.country || 'AT');

  const labelOptions = [
    { id: 'home', icon: 'IconHome', name: 'Home' },
    { id: 'work', icon: 'IconHandbag', name: 'Work' },
    { id: 'partner', icon: 'IconHeartCopy', name: 'Partner' },
    { id: 'other', icon: 'IconPlus', name: 'Other' },
  ];

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const isEdit = searchParams.has('edit');
    const isNew = searchParams.get('new') === 'true';
    const geoLatitude = searchParams.get('geoLatitude');
    const geoLongitude = searchParams.get('geoLongitude');
    const selectedAddress = searchParams.get('selectedAddress');
    const streetName = searchParams.get('streetName');
    const houseNumber = searchParams.get('houseNumber');
    const cityName = searchParams.get('cityName');
    const postalCode = searchParams.get('postalCode');
    const countryName = searchParams.get('countryName');

    if (geoLatitude && geoLongitude) {
      setFormValues((prev) => ({
        ...prev,
        geoLatitude: parseFloat(geoLatitude),
        geoLongitude: parseFloat(geoLongitude),
        streetName: streetName || prev.streetName || '',
        houseNumber: houseNumber || prev.houseNumber || '',
        cityName: cityName || prev.cityName || '',
        postalCode: postalCode || prev.postalCode || '',
        countryName: countryName || prev.countryName || ''
      }));
    }

    if (countryName) {
      setCountry({
        name: countryName,
      });
    }

    if (isEdit) {
      const editIntegrationId = searchParams.get('edit');
      setIntegrationId(editIntegrationId);
      if (!geoLatitude) {
        fetchAddressDetails(editIntegrationId);
      }
    }
  }, []);

  const fetchAddressDetails = async (integrationId) => {
    try {
      const response = await sendRequest({
        method: 'GET',
        url: `/module/address/getAddressByIntegrationId?integrationId=${integrationId}`,
      });
      if (response.status === 'success' && response.data) {
        console.log('response', response.data);
        setFormValues(response.data);
        setSelectedLabel(response.data.addressLabel || response.data.addressLable || null);
        setCountry({
          name: response.data.countryName || 'Austria',
        });
      }
    } catch (error) {
      console.error('Failed to fetch address details', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveAddress = async () => {
    if (isNew) {
      await addNewAddress();
    } else if (isEdit) {
      await updateAddress();
    }
  };

  const addNewAddress = async () => {
    try {
      let geoLatitude = formValues.geoLatitude;
      let geoLongitude = formValues.geoLongitude;
      
      // If coordinates are from location picker, use them; otherwise geocode
      if (!geoLatitude || !geoLongitude) {
        try {
          if (!window.mapkit) {
            console.error('MapKit not loaded');
            return;
          }
          
          if (!window.mapkit.isInitialized) {
            window.mapkit.init({
              authorizationCallback: (done) => {
                done(process.env.REACT_APP_APPLE_MAP_KEY);
              },
            });
          }
          
          const geocoder = new window.mapkit.Geocoder();
          const addressString = `${formValues.streetName} ${formValues.houseNumber}, ${formValues.cityName}, ${formValues.postalCode}, ${country.name}`;
          
          const geocodeResult = await new Promise((resolve, reject) => {
            geocoder.lookup(addressString, (error, data) => {
              if (error || !data.results || data.results.length === 0) {
                reject(error || new Error('No results found'));
              } else {
                resolve(data.results[0]);
              }
            });
          });
          
          geoLatitude = geocodeResult.coordinate.latitude;
          geoLongitude = geocodeResult.coordinate.longitude;
          handleInputChange('geoLatitude', geoLatitude);
          handleInputChange('geoLongitude', geoLongitude);
        } catch (geocodeError) {
          console.error('Geocoding failed:', geocodeError);
        }
      }
      
      const addressData = {
        belongsTo: user.integrationId,
        streetName: formValues.streetName || null,
        houseNumber: formValues.houseNumber || null,
        cityName: formValues.cityName || null,
        postalCode: formValues.postalCode || null,
        region: formValues.region || null,
        countryName: formValues.countryName || country.name || null,
        entrance: formValues.entrance || null,
        floor: formValues.floor || null,
        door: formValues.door || null,
        note: formValues.note || null,
        addressLable: selectedLabel || null,
        addressIcon: labelOptions.find(label => label.id === selectedLabel)?.icon || null,
        geoLatitude: geoLatitude || null,
        geoLongitude: geoLongitude || null,
      };
      
      const response = await sendRequest({
        method: 'POST',
        url: '/module/address/setAddress',
        data: { addressData },
      });
      if (response.status === 'success') {
        await updateUserAddresses();
        navigate('/address/');
      }
    } catch (error) {
      console.error('Failed to add new address', error);
    }
  };

  const updateAddress = async () => {
    try {
      let geoLatitude = formValues.geoLatitude;
      let geoLongitude = formValues.geoLongitude;
      
      try {
        if (!window.mapkit) {
          console.error('MapKit not loaded');
        } else {
          if (!window.mapkit.isInitialized) {
            window.mapkit.init({
              authorizationCallback: (done) => {
                done(process.env.REACT_APP_APPLE_MAP_KEY);
              },
            });
          }
          
          const geocoder = new window.mapkit.Geocoder();
          const addressString = `${formValues.streetName} ${formValues.houseNumber}, ${formValues.cityName}, ${formValues.postalCode}, ${country.name}`;
          
          const geocodeResult = await new Promise((resolve, reject) => {
            geocoder.lookup(addressString, (error, data) => {
              if (error || !data.results || data.results.length === 0) {
                reject(error || new Error('No results found'));
              } else {
                resolve(data.results[0]);
              }
            });
          });
          
          geoLatitude = geocodeResult.coordinate.latitude;
          geoLongitude = geocodeResult.coordinate.longitude;
          
          setFormValues(prev => ({
            ...prev,
            geoLatitude: geoLatitude,
            geoLongitude: geoLongitude
          }));
        }
      } catch (geocodeError) {
        console.error('Geocoding failed during update:', geocodeError);
      }
      
      const addressData = {
        streetName: formValues.streetName,
        houseNumber: formValues.houseNumber,
        cityName: formValues.cityName,
        postalCode: formValues.postalCode,
        countryName: country.name,
        entrance: formValues.entrance || null,
        floor: formValues.floor || null,
        door: formValues.door || null,
        note: formValues.note || null,
        addressLable: selectedLabel,
        addressIcon: labelOptions.find(label => label.id === selectedLabel)?.icon || null,
        geoLatitude: geoLatitude || null,
        geoLongitude: geoLongitude || null,
      };

      const response = await sendRequest({
        method: 'PUT',
        url: '/module/address/updateAddress',
        data: {
          integrationId: integrationId || formValues.integrationId,
          addressData
        },
      });
      if (response.status === 'success') {
        await updateUserAddresses();
        navigate('/address/');
      }
    } catch (error) {
      console.error('Failed to update address', error);
    }
  };

  const handleDeleteAddress = async () => {
    try {
      const response = await sendRequest({
        method: 'POST',
        url: `/module/address/deleteAddressByIntegrationId`,
        data: {
          integrationId: integrationId,
        },
      });
      if (response.status === 'success') {
        navigate('/address/');
      }
    } catch (error) {
      console.error('Failed to delete address', error);
    }
  };

  const handleBackPress = () => {
    window.history.back();
  };

  const searchParams = new URLSearchParams(window.location.search);
  const isEdit = searchParams.has('edit');
  const isNew = searchParams.get('new') === 'true';

  const getNavigateUrl = () => {
    let navigateUrl = '/address/location?';

    if (isEdit) {
      navigateUrl += `edit=${searchParams.get('edit')}&`;
    }
    if (isNew) {
      navigateUrl += `new=true&`;
    }

    if (formValues.cityName) {
      navigateUrl += `cityName=${formValues.cityName}&`;
    }

    return navigateUrl;
  };

  const handleNavigate = () => {
    const url = getNavigateUrl();
    window.location.href = url;
  };

  const handleCountrySelect = (country) => {
    setSelectedCountry(country);
    setCountryDrawerOpen(false);
  };

  return (
    <BringzzPageContainer
      header={{
        left: (
          <BringzzIcon
            size="22"
            icon="IconChevronLeft"
            folder="LineIcons"
            className="cursor-pointer"
            onClick={handleBackPress}
          />
        ),
        title: <BringzzText tag="h2">{isNew ? 'Add New Address' : 'Edit Address'}</BringzzText>,
      }}
    >
      <div className="p-6 space-y-6">
        
        <div className="flex items-center space-x-3">
          <BringzzIcon
            icon="IconGeoLocation"
            folder="HealthIcons"
            size="30"
            className="rounded-full bg-gray-300 text-white"
          />
          <div onClick={handleNavigate} className="cursor-pointer">
            <BringzzText tag="p" className="text-sm text-gray-500">
              {formValues.cityName || 'Select location'}{country?.name ? `, ${country.name}` : ''}
            </BringzzText>
            {formValues.streetName && (
              <BringzzText tag="p" className="text-xs text-gray-400">
                {formValues.streetName} {formValues.houseNumber}
              </BringzzText>
            )}
          </div>
        </div>
        <hr className="my-2 border-t-2 border-gray-300" />

        <BringzzText tag="h4" className="font-bold mb-2">
          Address label (optional)
        </BringzzText>
        <div className="flex justify-around">
          {labelOptions.map((label) => (
            <div
              key={label.id}
              className="flex flex-col cursor-pointer"
              onClick={() => setSelectedLabel(selectedLabel === label.id ? null : label.id)}
            >
              <div
                className={`w-10 h-10 flex items-center justify-center rounded-full border ${selectedLabel === label.id
                  ? 'bg-blue-500 border-blue-500'
                  : 'bg-gray-200 border-gray-200'
                  }`}
              >
                <BringzzIcon
                  size="24"
                  icon={label.icon}
                  folder="LineIcons"
                  className={selectedLabel === label.id ? 'text-white' : 'text-black'}
                />
              </div>
              <BringzzText tag="span" className="mt-1 text-sm">
                {label.name}
              </BringzzText>
            </div>
          ))}
        </div>

        <div className="flex gap-3">
          <BringzzInput
            id="streetName"
            placeholder="Musterstarsse"
            label="Street Name"
            value={formValues.streetName}
            onChange={(e) => handleInputChange('streetName', e.target.value)}
            containerClassname="w-full"
          />
          <BringzzInput
            id="houseNumber"
            placeholder="12"
            label="House Number"
            value={formValues.houseNumber}
            onChange={(e) => handleInputChange('houseNumber', e.target.value)}
            containerClassname="w-1/2"
          />
        </div>
        <div className="flex gap-3">
          <BringzzInput
            id="entrance"
            placeholder="entrance"
            label="Entrance (opt.)"
            value={formValues.entrance}
            onChange={(e) => handleInputChange('entrance', e.target.value)}
            containerClassname="w-full"
          />
          <BringzzInput
            id="floor"
            placeholder="floor"
            label="Floor (opt.)"
            value={formValues.floor}
            onChange={(e) => handleInputChange('floor', e.target.value)}
            containerClassname="w-full"
          />
          <BringzzInput
            id="door"
            label="Door (opt.)"
            placeholder="door"
            value={formValues.door}
            onChange={(e) => handleInputChange('door', e.target.value)}
            containerClassname="w-full"
          />
        </div>
        <div className="flex gap-3">
          <BringzzInput
            id="postalCode"
            placeholder="1040"
            label="Postal Code"
            value={formValues.postalCode}
            onChange={(e) => handleInputChange('postalCode', e.target.value)}
            containerClassname="w-1/3"
          />
          <BringzzInput
            id="cityName"
            label="City"
            placeholder="Wien"
            value={formValues.cityName}
            onChange={(e) => handleInputChange('cityName', e.target.value)}
            containerClassname="w-full"
          />
        </div>
        <div>
          <BringzzText tag="h4" className="font-bold mb-2">
            Country
          </BringzzText>
          <BringzzCountryDropdown
            className="w-full bg-[#F4F4F4] border border-gray-300 rounded-lg p-3 text-sm"
            mode="flag-name"
            value={country}
            onCountryChange={(selectedCountry) => {
              setCountry(selectedCountry);
              handleInputChange('countryName', selectedCountry.name);
            }}
          />
        </div>
        <BringzzInput
          id="note"
          label="Note to rider"
          placeholder="e.g. entrance description, doorbell, etc."
          value={formValues.note}
          onChange={(e) => handleInputChange('note', e.target.value)}
          containerClassname="w-full"
          inputClassname="resize-none h-24"
          as="textarea"
        />
      </div>
      <div className="px-6 pb-6 mb-20 flex justify-between gap-4">
        {!isNew && (
          <BringzzButton
            className="border w-full border-black justify-center"
            onClick={() => setModalOpen(true)}
          >
            Delete
          </BringzzButton>
        )}
        <BringzzButton className="bg-magic-lilac w-full justify-center" onClick={handleSaveAddress}>
          {isNew ? 'Add Address' : 'Save'}
        </BringzzButton>
      </div>
      {modalOpen && (
        <BringzzModal
          title={'Delete this address ?'}
          description={`This address will be permanently deleted.`}
          onCancel={() => setModalOpen(false)}
          confirmButtonText="Delete"
          onConfirm={handleDeleteAddress}
        />
      )}

      <BringzzBottomDrawer
        isOpen={countryDrawerOpen}
        close={() => setCountryDrawerOpen(false)}
        title={t('component.account.address.select_country')}
      >
        <div className='p-4'>
          <BringzzRadioList
            onChange={handleCountrySelect}
            value={selectedCountry}
            className='space-y-2'
            state={{
              checked: (
                <BringzzIcon
                  icon="IconCheck"
                  folder="LineIcons"
                  className="h-6 w-6 p-1 bg-blue-500 text-white rounded-full"
                  size="18"
                />
              ),
              unChecked: (
                <div className="h-6 w-6 rounded-full border border-gray-400"></div>
              ),
            }}
          >
            {COUNTRIES.map(country => (
              <BringzzRadio
                key={country.code}
                value={country.code}
                className='p-3 border rounded-lg'
              >
                {country.name}
              </BringzzRadio>
            ))}
          </BringzzRadioList>
        </div>
      </BringzzBottomDrawer>
    </BringzzPageContainer>
  );
};

export default EditAddressView;
